import { useState } from "react";
import type { ReactNode } from "react";
import Link from "next/link";
import { motion } from "motion/react";
import { Mail, Server, Webhook } from "lucide-react";
import { cn } from "@/lib/utils";

type Action = {
  label: string;
  href: string;
};

type Item = {
  name: string;
  icon?: ReactNode;
  action?: Action;
  demo?: Action;
  // "ghost" renders a dashed, background-coloured card (used for the bring-your-own option).
  ghost?: boolean;
  // "hidden" keeps a tile defined but out of the grid for now (frameworks we don't
  // surface yet). Filtered out at render — flip to false to bring one back.
  hidden?: boolean;
};

type FooterLink = {
  label: string;
  href: string;
};

type Section = {
  id: string;
  eyebrow: string;
  title: string;
  description: string;
  items: Item[];
  footer: {
    text: string;
    links: FooterLink[];
  };
};

// Brand marks live in /public/brands as flat SVGs. Most are colourful and read on any card.
// "tone" handles the monochrome ones via CSS filter so they follow the theme:
//   - "dark": a dark/black mark — flipped to white in dark mode
//   - "light": a white mark (e.g. Postgres) — flipped to dark in light mode
const brand = (slug: string, tone?: "dark" | "light"): ReactNode => (
  <img
    src={`/brands/${slug}.svg`}
    alt=""
    aria-hidden="true"
    className={cn(
      "h-9 w-9 object-contain",
      tone === "dark" && "dark:brightness-0 dark:invert",
      tone === "light" && "brightness-0 dark:brightness-100"
    )}
  />
);

// "Deploy" lands on the templates list pre-filtered to that framework, so it always
// points at real, deployable templates rather than a generic page.
const deploy = (term: string): Action => ({
  label: "Deploy",
  href: `/r?tag=${encodeURIComponent(term)}`
});
// Live demos only exist for the five native boilerplates with a backing dollardeploy
// example repo: nextjs, python, go, rust, java. Don't add demos without a real deploy.
const demo = (term: string): Action => ({
  label: "Demo",
  href: `https://${term}.demo.dollardeploy.dev/`
});
// Wiring an assistant up is a docs-led setup for now.
const MCP_SETUP: Action = { label: "Set up", href: "https://docs.dollardeploy.com" };

const SECTIONS: Section[] = [
  {
    id: "frameworks",
    eyebrow: "languages & frameworks",
    title: "Frameworks",
    description:
      "Point us at a repo and we detect the framework, build it, and run it on the right port. Next.js, Go, Rust, Java, and Python run natively out of the box. Anything with a Dockerfile or compose file works too.",
    // Out of the box: native Go/Rust/Next.js/Java + Docker. The five native boilerplates
    // each have a live demo; Docker is Deploy-only until a demo repo exists.
    items: [
      {
        name: "Next.js",
        icon: brand("nextdotjs", "dark"),
        action: deploy("nextjs"),
        demo: demo("nextjs")
      },
      {
        name: "Python",
        icon: brand("python"),
        action: deploy("python"),
        demo: demo("python")
      },
      { name: "Go", icon: brand("go"), action: deploy("golang"), demo: demo("go") },
      {
        name: "Rust",
        icon: brand("rust", "dark"),
        action: deploy("rust"),
        demo: demo("rust")
      },
      {
        name: "Spring",
        icon: brand("spring"),
        action: deploy("java"),
        demo: demo("java")
      },
      // Docker is supported out of the box but has no demo repo yet — Deploy only.
      { name: "Docker", icon: brand("docker"), action: deploy("docker") },
      // Hidden for now — supported via detection but not surfaced, and no live demo.
      // Drop a Dockerfile/compose and these all still deploy. Flip hidden to bring back.
      { name: "React", icon: brand("react"), action: deploy("react"), hidden: true },
      { name: "Vite", icon: brand("vite"), action: deploy("vite"), hidden: true },
      { name: "Node.js", icon: brand("nodedotjs"), action: deploy("node"), hidden: true },
      {
        name: "Express",
        icon: brand("express", "dark"),
        action: deploy("express"),
        hidden: true
      },
      { name: "Django", icon: brand("django"), action: deploy("django"), hidden: true },
      {
        name: "Flask",
        icon: brand("flask", "light"),
        action: deploy("flask"),
        hidden: true
      },
      { name: "FastAPI", icon: brand("fastapi"), action: deploy("fastapi"), hidden: true },
      { name: "PHP", icon: brand("php", "dark"), action: deploy("php"), hidden: true },
      { name: "Laravel", icon: brand("laravel"), action: deploy("laravel"), hidden: true }
    ],
    footer: {
      text: "And plenty more.",
      links: [{ label: "Browse templates", href: "/r" }]
    }
  },
  {
    id: "providers",
    eyebrow: "your own servers",
    title: "Cloud providers",
    description:
      "Connect a provider account and we provision the server, secure it, and deploy over SSH. You stay in control and pay the provider directly.",
    items: [
      { name: "DigitalOcean", icon: brand("digitalocean") },
      { name: "Verda Cloud", icon: brand("verda", "dark") },
      { name: "Hetzner", icon: brand("hetzner") },
      {
        name: "Bring your own",
        ghost: true,
        action: {
          label: "Bring your own",
          href: "https://docs.dollardeploy.com/getting-started"
        }
      }
    ],
    footer: {
      text: "Already have a server?",
      links: [{ label: "Connect it", href: "https://docs.dollardeploy.com/getting-started" }]
    }
  },
  {
    id: "channels",
    eyebrow: "deploy alerts",
    title: "Notification channels",
    description:
      "Send deploy results, build status, and health alerts to the tools you already use. Connect once and you're set.",
    items: [
      { name: "Slack", icon: brand("slack") },
      { name: "Discord", icon: brand("discord") },
      { name: "Telegram", icon: brand("telegram") },
      { name: "Email", icon: <Mail className="h-9 w-9 text-primary" strokeWidth={1.5} /> },
      { name: "Webhook", icon: <Webhook className="h-9 w-9 text-primary" strokeWidth={1.5} /> }
    ],
    footer: {
      text: "Set up in about a minute.",
      links: [{ label: "View the docs", href: "https://docs.dollardeploy.com/notifications" }]
    }
  },
  {
    id: "ai-assistants",
    eyebrow: "from your editor",
    title: "AI assistants",
    description:
      "Run our MCP server and your assistant can deploy, read logs, and restart apps for you, without leaving the editor.",
    items: [
      { name: "Cursor", icon: brand("cursor", "dark"), action: MCP_SETUP },
      { name: "Claude Code", icon: brand("claude"), action: MCP_SETUP },
      { name: "MCP", icon: brand("mcp", "dark"), action: MCP_SETUP }
    ],
    footer: {
      text: "Works with any MCP client.",
      links: [
        { label: "CLI", href: "https://www.npmjs.com/package/@dollardeploy/cli" },
        { label: "API", href: "/apidocs" }
      ]
    }
  },
  {
    id: "managed-services",
    eyebrow: "databases & caches",
    title: "Managed services",
    description:
      "Add Postgres, Redis, or a queue to any host in one click. The connection string is injected into your app's environment automatically.",
    items: [
      { name: "PostgreSQL", icon: brand("postgresql", "dark") },
      { name: "Redis", icon: brand("redis") },
      { name: "MariaDB", icon: brand("mariadb", "dark") },
      { name: "MongoDB", icon: brand("mongodb") }
    ],
    footer: {
      text: "More added regularly.",
      links: [
        { label: "View the docs", href: "https://docs.dollardeploy.com/managed-services" }
      ]
    }
  }
];

const isExternal = (href: string) => href.startsWith("http");

// One resting + hover surface shared by every tile, so the whole grid feels alive
// rather than only the cards that flip. Lift + ring + shadow on hover.
const cardBase = "bg-card border rounded-xl py-5 px-3";
const cardHover =
  "transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-0.5 hover:shadow-md hover:ring-1 hover:ring-foreground/10 hover:border-foreground/20";

// Shared face styling for both sides of a flip card. The lift comes from the wrapper
// (it owns the hover), so the faces only pick up the ring + shadow via group-hover.
const faceClass = cn(
  "absolute inset-0 flex flex-col items-center justify-center gap-2.5 [backface-visibility:hidden]",
  cardBase,
  "transition-shadow duration-200 group-hover:shadow-md group-hover:ring-1 group-hover:ring-foreground/10"
);

// Just a fixed-size slot — no background. Dark marks are handled per-icon via `brand(tone)`.
const IconBadge = ({ children }: { children: ReactNode }) => (
  <span className="size-12 flex items-center justify-center shrink-0">{children}</span>
);

const Label = ({ children }: { children: ReactNode }) => (
  <span className="text-[13px] font-medium text-secondary-foreground text-center">
    {children}
  </span>
);

// Dashed, background-coloured card for the "bring your own" option.
const GhostTile = ({ item }: { item: Item }) => (
  <Link
    href={item.action ? item.action.href : "#"}
    target={item.action && isExternal(item.action.href) ? "_blank" : undefined}
    rel={item.action && isExternal(item.action.href) ? "noreferrer noopener" : undefined}
    className="group flex flex-col items-center justify-center gap-2.5 rounded-xl border border-dashed bg-background py-5 px-3 text-center transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-0.5 hover:shadow-md hover:border-foreground/30 hover:bg-card"
  >
    <Server
      className="size-9 text-muted-foreground group-hover:text-foreground transition-colors"
      strokeWidth={1.5}
    />
    <span className="text-[13px] font-medium text-muted-foreground group-hover:text-foreground transition-colors">
      {item.name}
    </span>
  </Link>
);

// Plain card for items with no instant action (providers, channels, services).
const StaticTile = ({ item }: { item: Item }) => (
  <div
    className={cn("flex flex-col items-center justify-center gap-2.5", cardBase, cardHover)}
  >
    <IconBadge>{item.icon}</IconBadge>
    <Label>{item.name}</Label>
  </div>
);

// Card that flips to reveal its action on hover/focus.
const FlipTile = ({ item, action }: { item: Item; action: Action }) => {
  const [flipped, setFlipped] = useState(false);

  const handleEnter = () => setFlipped(true);
  const handleLeave = () => setFlipped(false);

  return (
    <div
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocusCapture={handleEnter}
      onBlurCapture={handleLeave}
      className="group relative [perspective:900px] transition-transform duration-200 hover:-translate-y-0.5"
    >
      {/* Spacer keeps the grid cell sized to the card while the faces are absolute. */}
      <div className="invisible flex flex-col items-center justify-center gap-2.5 py-5 px-3">
        <span className="size-12" />
        <Label>{item.name}</Label>
      </div>
      <motion.div
        initial={false}
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 0.45, ease: "easeInOut" }}
        style={{ transformStyle: "preserve-3d" }}
        className="absolute inset-0"
      >
        <div className={cn(faceClass, flipped && "pointer-events-none")}>
          <IconBadge>{item.icon}</IconBadge>
          <Label>{item.name}</Label>
        </div>
        <div
          className={cn(
            faceClass,
            "[transform:rotateY(180deg)]",
            !flipped && "pointer-events-none"
          )}
        >
          <div className="flex flex-col gap-2">
            {item.demo && (
              <Link
                href={item.demo.href}
                target={isExternal(item.demo.href) ? "_blank" : undefined}
                rel={isExternal(item.demo.href) ? "noreferrer noopener" : undefined}
                aria-label={`${item.demo.label} ${item.name}`}
                className="inline-flex items-center justify-center rounded-lg border border-foreground/20 text-foreground px-4 py-2 text-sm font-medium hover:bg-foreground/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {item.demo.label}
              </Link>
            )}
            <Link
              href={action.href}
              target={isExternal(action.href) ? "_blank" : undefined}
              rel={isExternal(action.href) ? "noreferrer noopener" : undefined}
              aria-label={`${action.label} ${item.name}`}
              className="inline-flex items-center justify-center rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {action.label}
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const Tile = ({ item }: { item: Item }) => {
  if (item.ghost) {
    return <GhostTile item={item} />;
  }
  if (item.action) {
    return <FlipTile item={item} action={item.action} />;
  }
  return <StaticTile item={item} />;
};

const FooterLinks = ({ text, links }: Section["footer"]) => (
  <div className="mt-6 text-sm text-muted-foreground">
    {text}{" "}
    {links.map((link, index) => (
      <span key={link.href}>
        {index > 0 && <span className="mx-1 text-muted-foreground/50">·</span>}
        <Link
          href={link.href}
          target={isExternal(link.href) ? "_blank" : undefined}
          rel={isExternal(link.href) ? "noreferrer noopener" : undefined}
          className="text-primary border-b border-dashed border-primary/40 hover:text-primary/80 transition-colors"
        >
          {link.label} →
        </Link>
      </span>
    ))}
  </div>
);

const IntegrationSection = ({ section }: { section: Section }) => (
  <section id={section.id} className="scroll-mt-24">
    <div className="max-w-[640px] mb-8">
      <div className="inline-block text-xs text-primary bg-primary/8 px-3 py-1 rounded-full mb-3 font-medium tracking-wide lowercase font-mono">
        {section.eyebrow}
      </div>
      <h2 className="font-serif text-[clamp(26px,3.5vw,34px)] leading-tight tracking-tight font-normal mb-2.5 text-foreground">
        {section.title}
      </h2>
      <p className="text-[15px] leading-relaxed text-muted-foreground text-pretty">
        {section.description}
      </p>
    </div>
    <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
      {section.items
        .filter(item => !item.hidden)
        .map(item => (
          <Tile key={item.name} item={item} />
        ))}
    </div>
    <FooterLinks text={section.footer.text} links={section.footer.links} />
  </section>
);

export const IntegrationsPage = () => (
  <div className="landing-dots font-sans text-foreground bg-background antialiased">
    <div className="max-w-[1080px] mx-auto px-6 sm:px-8">
      <header className="max-w-[760px] mx-auto text-center pt-20 pb-16">
        <div className="inline-block text-xs text-primary bg-primary/8 px-3 py-1 rounded-full mb-4 font-medium tracking-wide lowercase font-mono">
          integrations
        </div>
        <h1 className="font-serif text-[clamp(38px,6vw,60px)] leading-[1.05] tracking-tight font-normal mb-4 text-foreground text-balance">
          Works with the tools <em className="text-primary">you already use.</em>
        </h1>
        <p className="text-[17px] leading-relaxed text-muted-foreground text-pretty">
          Frameworks, servers, databases, and the channels you watch. Connect what you need and
          ignore the rest.
        </p>
      </header>

      <div className="flex flex-col gap-20 pb-12">
        {SECTIONS.map(section => (
          <IntegrationSection key={section.id} section={section} />
        ))}
      </div>

      <div className="border border-dashed rounded-2xl px-6 sm:px-8 py-8 my-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
        <div className="max-w-[560px]">
          <h2 className="font-serif text-3xl tracking-tight font-normal mb-1.5 text-foreground">
            Don&apos;t see what you need?
          </h2>
          <p className="text-[15px] leading-relaxed text-muted-foreground text-pretty">
            We ship new frameworks and integrations regularly. Tell us what you&apos;re running
            and we&apos;ll take a look.
          </p>
        </div>
        <Link
          href="mailto:hello@dollardeploy.com?subject=Integration%20request"
          className="shrink-0 inline-flex items-center rounded-full border px-5 py-2.5 text-sm font-medium hover:bg-foreground hover:text-background hover:border-foreground transition-colors"
        >
          Request it
        </Link>
      </div>
    </div>
  </div>
);
